import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useAuth, useTheme } from '../App';
import { PageLayout } from '../components/Sidebar';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../components/ui/dialog';
import { Checkbox } from '../components/ui/checkbox';
import { toast } from 'sonner';
import { format, parseISO } from 'date-fns';
import {
  Search, Edit2, Trash2, Filter, ChevronUp, ChevronDown, ChevronRight, ChevronLeft,
  Upload, Download, FileText, AlertCircle, Database, Square, CheckSquare,
  Minus, Check, X, Pencil, Save, AlertTriangle, Loader2
} from 'lucide-react';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '../components/ui/dropdown-menu';
import { PRIORITY_META, getTextColorForBg } from '../components/dashboard/TaskCard';
import { useWorkAreas } from '../contexts/WorkAreaContext';
import TaskModal from '../components/dashboard/TaskModal';
import DatePickerWithStats from '../components/dashboard/DatePickerWithStats';
import WorkAreaEditorModal from '../components/dashboard/WorkAreaEditorModal';

// Priority options for filters and selects
const PRIORITY_OPTIONS = [
  { value: -1, label: 'P-1' },
  { value: 0, label: 'P0' },
  { value: 1, label: 'P1' },
  { value: 2, label: 'P2' },
  { value: 3, label: 'P3' },
  { value: 4, label: 'P4' },
  { value: 5, label: 'P5' },
];

const API = '/api';

// Configuration
const PAGE_SIZE_OPTIONS = [25, 50, 100, 200];

const STATUS_OPTIONS = ['To Do', 'In Progress', 'Partial', 'Blocked', 'Done'];
const EFFORT_OPTIONS = [1, 2, 3, 5, 8, 13];

// Status colors
const STATUS_COLORS = {
  'To Do': 'bg-slate-100 dark:bg-slate-700 orka-text-secondary',
  'In Progress': 'bg-blue-100 text-blue-700',
  'Partial': 'bg-cyan-100 dark:bg-cyan-900/30 text-cyan-700',
  'Blocked': 'bg-red-100 text-red-700',
  'Done': 'bg-green-100 text-green-700'
};

const DataView = ({ embedded = false }) => {
  const { user, logout, actingAs } = useAuth();
  const { theme, setTheme } = useTheme();
  const { workAreaNames, workAreaColors, refresh: refreshWorkAreas } = useWorkAreas();
  const WORK_AREA_COLORS = workAreaColors;
  const fileInputRef = useRef(null);
  const importTasksRef = useRef([]);

  // Data state
  const [allTasks, setAllTasks] = useState([]);
  const [totalTaskCount, setTotalTaskCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [showWorkAreaEditor, setShowWorkAreaEditor] = useState(false);
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);
  
  // Selection state
  const [selectedTasks, setSelectedTasks] = useState(new Set());
  
  // Filter state
  const [showFilters, setShowFilters] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [dateIsEmpty, setDateIsEmpty] = useState(false);
  const [statusFilters, setStatusFilters] = useState(new Set());
  const [priorityFilters, setPriorityFilters] = useState(new Set());
  const [workAreaFilters, setWorkAreaFilters] = useState(new Set());
  const [workAreaIsEmpty, setWorkAreaIsEmpty] = useState(false);
  const [filterApplied, setFilterApplied] = useState(false);
  
  // Sort state
  const [sortField, setSortField] = useState('task_delivery_date');
  const [sortDirection, setSortDirection] = useState('desc');
  
  // Edit state
  const [editingCell, setEditingCell] = useState(null); // { taskId, field }
  const [editValue, setEditValue] = useState('');
  const [pendingEdits, setPendingEdits] = useState({}); // { taskId: { field: value } }
  
  // Modal state
  const [showBulkEdit, setShowBulkEdit] = useState(false);
  const [bulkEditField, setBulkEditField] = useState('');
  const [bulkEditValue, setBulkEditValue] = useState('');
  const [showImportModal, setShowImportModal] = useState(false);
  const [importStep, setImportStep] = useState(1); // 1 = upload+mapping, 2 = preview
  const [importHeaders, setImportHeaders] = useState([]); // CSV headers
  const [importStagingId, setImportStagingId] = useState(null); // Server-side staging ID
  const [importRowCount, setImportRowCount] = useState(0);
  const [importSampleRow, setImportSampleRow] = useState(null);
  const [columnMapping, setColumnMapping] = useState({}); // CSV column -> DB field
  const [mappingValidation, setMappingValidation] = useState({}); // colIdx -> { field, invalid, total, samples }
  const [rowMismatch, setRowMismatch] = useState({ count: 0, samples: [] });
  const [importFileText, setImportFileText] = useState(null); // Raw CSV text for re-parsing with overrides
  const [importFileKind, setImportFileKind] = useState(null); // 'csv' | 'json'
  const [importData, setImportData] = useState([]);
  const [importErrors, setImportErrors] = useState([]);
  const [importing, setImporting] = useState(false);
  const [importProgress, setImportProgress] = useState(null); // "Uploading..." / "Processing..." / null
  const [showDeleteAllModal, setShowDeleteAllModal] = useState(false);
  const [deleteConfirmText, setDeleteConfirmText] = useState('');
  const [deleting, setDeleting] = useState(false);
  const [deleteMode, setDeleteMode] = useState('soft'); // 'soft' or 'purge'
  const [showPurgeConfirm, setShowPurgeConfirm] = useState(false);
  const [showImportDialog, setShowImportDialog] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  
  // Edit task modal state
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingTaskData, setEditingTaskData] = useState(null);

  // ALL Database fields for mapping - comprehensive list
  // Field type tags — used for strict per-column validation during import.
  // Types:
  //   text        → any non-empty string
  //   date        → YYYY-MM-DD (also accepts common variants and coerces)
  //   time        → HH:MM (24h)
  //   int         → integer string
  //   int_range   → integer in a specific numeric set
  //   effort      → Fibonacci-ish set {0,1,2,3,5,8,13}
  //   priority    → int in {-1..5}
  //   bool        → true|false|yes|no|1|0
  //   enum_status → one of a fixed set
  //   sequence    → single letter A-Z
  //   links       → any string; will be normalised to array on the server
  // Canonical field map (post-Feb-2026 schema cleanup).
  // Status uses canonical keys: open | inProgress | partial | blocked | done | archived | cancelled
  // Date/time use the dual-time-model fields (task_local_*).
  const DB_FIELDS = [
    { key: 'task_title', label: 'Title', required: true, type: 'text' },
    { key: 'task_description', label: 'Description (HTML supported)', required: false, type: 'text' },
    { key: 'task_status_key', label: 'Status (open/inProgress/partial/blocked/done)', required: false, type: 'enum_status' },
    { key: 'task_priority', label: 'Priority (-1 to 5)', required: false, type: 'priority' },
    { key: 'task_local_date', label: 'Date (YYYY-MM-DD)', required: false, type: 'date' },
    { key: 'task_local_start_time', label: 'Start Time (HH:MM)', required: false, type: 'time' },
    { key: 'task_local_end_time', label: 'End Time (HH:MM)', required: false, type: 'time' },
    { key: 'task_local_timezone_context', label: 'Timezone (IANA)', required: false, type: 'text' },
    { key: 'work_area_id', label: 'Work Area ID', required: false, type: 'text' },
    { key: 'personal_sub_area_id', label: 'Personal Sub-Area ID', required: false, type: 'text' },
    { key: 'task_estimated_effort', label: 'Estimated Effort (1,2,3,5,8,13)', required: false, type: 'effort' },
    { key: 'task_actual_effort', label: 'Actual Effort', required: false, type: 'effort' },
    { key: 'task_actual_personal_effort', label: 'Actual Personal Effort', required: false, type: 'effort' },
    { key: 'task_sequence', label: 'Daily Sequencing (A-Z)', required: false, type: 'sequence' },
    { key: 'next_steps', label: 'Next Steps', required: false, type: 'text' },
    { key: 'big_picture_id', label: 'Related Epic', required: false, type: 'text' },
    { key: 'impact', label: 'Impact', required: false, type: 'text' },
    { key: 'responsible_person_id', label: 'Responsible Person ID', required: false, type: 'text' },
    { key: 'observers', label: 'Observers (comma-separated user_ids)', required: false, type: 'text' },
    { key: 'blocked_reason', label: 'Blocked Reason', required: false, type: 'text' },
    { key: 'task_related_links', label: 'Related Links (comma/space-separated)', required: false, type: 'links' },
    { key: 'task_is_meeting', label: 'Is Meeting (true/false)', required: false, type: 'bool' },
    { key: 'task_hidden', label: 'Is Suppressed (true/false)', required: false, type: 'bool' },
    { key: 'google_meet_link', label: 'Google Meet Link', required: false, type: 'text' },
    { key: 'calendar_html_link', label: 'Calendar Link', required: false, type: 'text' },
    { key: 'meeting_location', label: 'Meeting Location', required: false, type: 'text' },
    { key: 'meeting_organizer', label: 'Meeting Organizer', required: false, type: 'text' },
    { key: 'task_reschedule_count', label: 'Reschedule Count', required: false, type: 'int' },
  ];

  // Friendly aliases — accepted on IMPORT for back-compat with files exported
  // before the Feb-2026 schema cleanup. Translated to canonical names server-side.
  const IMPORT_LEGACY_ALIASES = {
    task_status: 'task_status_key',
    task_delivery_date: 'task_local_date',
    task_start_time: 'task_local_start_time',
    task_end_time: 'task_local_end_time',
    personal_sub_area: 'personal_sub_area_id', // requires sub-area name → id resolution server-side
    responsible_person_name: '__derived__',     // ignored on import (look up by id)
    created_by_display: '__derived__',
    task_estimated_minutes: '__derived__',
    task_duration_minutes: '__derived__',
  };

  // Validators — return `null` if the value is valid for the given type, otherwise an error string.
  // Empty strings are ALWAYS valid (the field is simply not set on that row).
  const ALLOWED_STATUSES = [
    // Canonical
    'open', 'inProgress', 'partial', 'blocked', 'done', 'archived', 'cancelled',
    // Legacy strings still accepted (auto-mapped server-side)
    'To Do', 'In Progress', 'Blocked', 'Done', 'Completed', 'Cancelled', 'Partial', 'Archived',
  ];
  const validateImportValue = (type, rawValue) => {
    const value = (rawValue == null ? '' : String(rawValue)).trim();
    if (!value) return null;
    switch (type) {
      case 'date': {
        // Strict: blank is OK (handled above), otherwise must be exactly YYYY-MM-DD.
        if (/^\d{4}-\d{2}-\d{2}$/.test(value)) return null;
        return 'not a date (expected YYYY-MM-DD)';
      }
      case 'time': {
        if (/^\d{1,2}:\d{2}(:\d{2})?$/.test(value)) return null;
        return 'not a time (expected HH:MM)';
      }
      case 'int': {
        if (/^-?\d+$/.test(value)) return null;
        return 'not an integer';
      }
      case 'priority': {
        if (/^-?\d+$/.test(value)) {
          const n = parseInt(value, 10);
          if (n >= -1 && n <= 5) return null;
        }
        return 'priority must be -1..5';
      }
      case 'effort': {
        if (/^\d+$/.test(value)) {
          const n = parseInt(value, 10);
          if ([0, 1, 2, 3, 5, 8, 13].includes(n)) return null;
        }
        return 'effort must be 0, 1, 2, 3, 5, 8, or 13';
      }
      case 'bool': {
        if (/^(true|false|yes|no|1|0)$/i.test(value)) return null;
        return 'must be true/false';
      }
      case 'enum_status': {
        const match = ALLOWED_STATUSES.find(s => s.toLowerCase() === value.toLowerCase());
        if (match) return null;
        return `status must be one of: ${ALLOWED_STATUSES.join(', ')}`;
      }
      case 'sequence': {
        if (/^[A-Za-z]$/.test(value)) return null;
        return 'sequence must be a single letter A-Z';
      }
      case 'links':
      case 'text':
      default:
        return null;
    }
  };

  // Normalize a value to the canonical shape Orka expects (e.g. coerce date formats
  // to YYYY-MM-DD, priorities/efforts to integers, bools to true/false strings).
  const normalizeImportValue = (type, rawValue) => {
    const value = (rawValue == null ? '' : String(rawValue)).trim();
    if (!value) return '';
    switch (type) {
      case 'date': {
        // Strict: only canonical YYYY-MM-DD — nothing else is coerced.
        return /^\d{4}-\d{2}-\d{2}$/.test(value) ? value : '';
      }
      case 'time': {
        const m = value.match(/^(\d{1,2}):(\d{2})(:\d{2})?$/);
        if (m) return `${m[1].padStart(2, '0')}:${m[2]}`;
        return value;
      }
      case 'priority':
      case 'int':
      case 'effort':
        return /^-?\d+$/.test(value) ? String(parseInt(value, 10)) : value;
      case 'bool':
        return /^(true|yes|1)$/i.test(value) ? 'true' : /^(false|no|0)$/i.test(value) ? 'false' : value;
      case 'enum_status': {
        const match = ALLOWED_STATUSES.find(s => s.toLowerCase() === value.toLowerCase());
        return match || value;
      }
      default:
        return value;
    }
  };

  // Template CSV content — uses canonical field names (post-cleanup schema).
  const generateTemplate = () => {
    const headers = DB_FIELDS.map(f => f.key).join(',');
    const exampleRow = [
      'Example Task Title',
      'Task description with <b>HTML</b> support',
      'inProgress',                    // task_status_key
      '2',                             // task_priority
      '2026-03-01',                    // task_local_date
      '09:00',                         // task_local_start_time
      '10:00',                         // task_local_end_time
      'Australia/Sydney',              // task_local_timezone_context
      '',                              // work_area_id
      '',                              // personal_sub_area_id
      '3',                             // task_estimated_effort
      '',                              // task_actual_effort
      '',                              // task_actual_personal_effort
      'A',                             // task_sequence
      'Complete the report',           // next_steps
      '',                              // big_picture_id
      'High business value',           // impact
      '',                              // responsible_person_id
      '',                              // observers (comma-sep user_ids)
      '',                              // blocked_reason
      'https://example.com',           // task_related_links
      'false',                         // task_is_meeting
      'false',                         // task_hidden
      '',                              // google_meet_link
      '',                              // calendar_html_link
      '',                              // meeting_location
      '',                              // meeting_organizer
      '0',                             // task_reschedule_count
    ].join(',');
    return `${headers}\n${exampleRow}`;
  };

  const downloadTemplate = () => {
    const content = generateTemplate();
    const blob = new Blob([content], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'orka_import_template.csv';
    a.click();
    URL.revokeObjectURL(url);
    toast.success('Template downloaded');
  };

  // Get unique work areas from data
  const uniqueWorkAreas = useMemo(() => {
    const areas = new Set(allTasks.map(t => t.work_area_id).filter(Boolean));
    return Array.from(areas);
  }, [allTasks]);

  // Fetch all tasks
  const fetchTasks = useCallback(async () => {
    if (!user) return;
    setLoading(true);
    try {
      const params = new URLSearchParams({ 
        all_tasks: 'true',
        include_suppressed: 'true',
        include_deleted: 'false',
        include_observed: 'true'
      });
      if (actingAs) params.append('acting_as', actingAs);
      const response = await fetch(`${API}/tasks?${params}`, { credentials: 'include' });
      if (response.ok) {
        const data = await response.json();
        setAllTasks(data);
        setTotalTaskCount(data.length);
      }
    } catch (error) {
      toast.error('Failed to load tasks');
    } finally {
      setLoading(false);
    }
  }, [user, actingAs]);

  useEffect(() => { fetchTasks(); }, [fetchTasks]);

  // Filter and sort
  const filteredTasks = useMemo(() => {
    let result = allTasks;
    
    // Apply filters if filterApplied is true or searching
    if (filterApplied || searchQuery) {
      result = allTasks.filter(task => {
        // Search filter (always active)
        if (searchQuery) {
          const query = searchQuery.toLowerCase();
          if (!task.task_title.toLowerCase().includes(query) && 
              !(task.task_description || '').toLowerCase().includes(query)) {
            return false;
          }
        }
        
        // Only apply other filters if filterApplied
        if (filterApplied) {
          // Date is empty filter
          if (dateIsEmpty) {
            if (task.task_local_date) return false;
          } else {
            // Date range filter - only apply if task has a date
            if (dateFrom || dateTo) {
              if (!task.task_local_date) return false; // Exclude tasks with no date when date range is set
              if (dateFrom && task.task_local_date < dateFrom) return false;
              if (dateTo && task.task_local_date > dateTo) return false;
            }
          }
          
          // Status filter
          if (statusFilters.size > 0 && !statusFilters.has(task.task_status)) return false;
          
          // Priority filter
          if (priorityFilters.size > 0 && !priorityFilters.has(task.task_priority)) return false;
          
          // Work area filter
          if (workAreaIsEmpty) {
            // Only show tasks with empty/null work_area
            if (task.work_area_id && task.work_area_id !== 'General') return false;
          } else if (workAreaFilters.size > 0 && !workAreaFilters.has(task.work_area_id)) {
            return false;
          }
        }
        
        return true;
      });
    }

    // Always sort (regardless of filters)
    result = [...result].sort((a, b) => {
      let aVal = a[sortField];
      let bVal = b[sortField];
      
      // Handle null/undefined
      if (aVal == null && bVal == null) return 0;
      if (aVal == null) return 1;
      if (bVal == null) return -1;
      
      // Numeric fields
      if (['task_priority', 'task_estimated_effort', 'task_actual_effort', 'task_actual_personal_effort'].includes(sortField)) {
        aVal = Number(aVal);
        bVal = Number(bVal);
      }
      
      if (aVal < bVal) return sortDirection === 'asc' ? -1 : 1;
      if (aVal > bVal) return sortDirection === 'asc' ? 1 : -1;
      return 0;
    });

    return result;
  }, [allTasks, searchQuery, dateFrom, dateTo, dateIsEmpty, statusFilters, priorityFilters, workAreaFilters, workAreaIsEmpty, sortField, sortDirection, filterApplied]);

  // Apply pagination to filtered results
  const displayedTasks = useMemo(() => {
    const startIndex = (currentPage - 1) * pageSize;
    const endIndex = startIndex + pageSize;
    return filteredTasks.slice(startIndex, endIndex);
  }, [filteredTasks, currentPage, pageSize]);

  // Pagination info
  const totalPages = Math.ceil(filteredTasks.length / pageSize);
  const startItem = filteredTasks.length === 0 ? 0 : (currentPage - 1) * pageSize + 1;
  const endItem = Math.min(currentPage * pageSize, filteredTasks.length);
  const hasFilters = filterApplied || searchQuery;
  const activeFilterCount = (searchQuery ? 1 : 0) + (filterApplied && (dateFrom || dateTo || dateIsEmpty) ? 1 : 0) + statusFilters.size + priorityFilters.size + workAreaFilters.size + (workAreaIsEmpty ? 1 : 0);
  const pendingEditCount = Object.keys(pendingEdits).length;
  
  // Reset to page 1 when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery, dateFrom, dateTo, dateIsEmpty, statusFilters, priorityFilters, workAreaFilters, workAreaIsEmpty, filterApplied]);

  // Toggle functions
  const toggleSort = (field) => {
    if (sortField === field) {
      setSortDirection(d => d === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const toggleStatusFilter = (status) => {
    setStatusFilters(prev => {
      const next = new Set(prev);
      if (next.has(status)) next.delete(status);
      else next.add(status);
      return next;
    });
  };

  const togglePriorityFilter = (priority) => {
    setPriorityFilters(prev => {
      const next = new Set(prev);
      if (next.has(priority)) next.delete(priority);
      else next.add(priority);
      return next;
    });
  };

  const toggleWorkAreaFilter = (area) => {
    setWorkAreaFilters(prev => {
      const next = new Set(prev);
      if (next.has(area)) next.delete(area);
      else next.add(area);
      return next;
    });
  };

  const clearAllFilters = () => {
    setSearchQuery('');
    setDateFrom('');
    setDateTo('');
    setStatusFilters(new Set());
    setPriorityFilters(new Set());
    setWorkAreaFilters(new Set());
  };

  const toggleTaskSelection = (taskId) => {
    setSelectedTasks(prev => {
      const next = new Set(prev);
      if (next.has(taskId)) next.delete(taskId);
      else next.add(taskId);
      return next;
    });
  };

  const toggleSelectAll = () => {
    if (selectedTasks.size === displayedTasks.length) {
      setSelectedTasks(new Set());
    } else {
      setSelectedTasks(new Set(displayedTasks.map(t => t.task_id)));
    }
  };

  // Edit functions
  const startEdit = (taskId, field, currentValue) => {
    setEditingCell({ taskId, field });
    setEditValue(currentValue ?? '');
  };
  
  // Open full edit modal for a task
  const openEditModal = (task) => {
    setEditingTaskData(task);
    setShowEditModal(true);
  };
  
  // Handle save from edit modal
  const handleEditModalSave = async (updatedTask) => {
    // Update local state
    setAllTasks(prev => prev.map(t => 
      t.task_id === updatedTask.task_id ? { ...t, ...updatedTask } : t
    ));
    setShowEditModal(false);
    setEditingTaskData(null);
    toast.success('Task updated');
  };

  const cancelEdit = () => {
    setEditingCell(null);
    setEditValue('');
  };

  const saveEdit = () => {
    if (!editingCell) return;
    const { taskId, field } = editingCell;
    
    // Save immediately to backend
    updateTaskField(taskId, field, editValue);
    
    cancelEdit();
  };

  const saveAllChanges = async () => {
    const taskIds = Object.keys(pendingEdits);
    if (taskIds.length === 0) return;

    try {
      const params = new URLSearchParams();
      if (actingAs) params.append('acting_as', actingAs);

      let successCount = 0;
      for (const taskId of taskIds) {
        const updates = pendingEdits[taskId];
        const response = await fetch(`${API}/tasks/${taskId}?${params}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify(updates)
        });
        if (response.ok) successCount++;
      }

      toast.success(`Saved ${successCount} changes`);
      setPendingEdits({});
      fetchTasks();
    } catch {
      toast.error('Failed to save changes');
    }
  };

  const updateTaskField = async (taskId, field, value) => {
    try {
      const params = new URLSearchParams();
      if (actingAs) params.append('acting_as', actingAs);
      const response = await fetch(`${API}/tasks/${taskId}?${params}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ [field]: value })
      });
      if (response.ok) {
        setAllTasks(prev => prev.map(t => t.task_id === taskId ? { ...t, [field]: value } : t));
        if (field === 'task_hidden') {
          toast.success(value ? 'Task hidden from effort calculations' : 'Task effort now counted');
        }
      }
    } catch {
      toast.error('Failed to update');
      fetchTasks();
    }
  };

  const deleteTask = async (taskId) => {
    try {
      const params = new URLSearchParams();
      if (actingAs) params.append('acting_as', actingAs);
      const response = await fetch(`${API}/tasks/${taskId}?${params}`, { method: 'DELETE', credentials: 'include' });
      if (response.ok) {
        toast.success('Task deleted');
        setAllTasks(prev => prev.filter(t => t.task_id !== taskId));
        setTotalTaskCount(prev => prev - 1);
      }
    } catch {
      toast.error('Failed to delete task');
    }
  };

  // Bulk edit
  const applyBulkEdit = async () => {
    if (selectedTasks.size === 0 || !bulkEditField || bulkEditValue === '') {
      toast.error('Please select tasks and fill in all fields');
      return;
    }

    try {
      const params = new URLSearchParams();
      if (actingAs) params.append('acting_as', actingAs);
      
      const updates = {};
      if (bulkEditField === 'task_status') updates.task_status = bulkEditValue;
      else if (bulkEditField === 'task_priority') updates.task_priority = parseInt(bulkEditValue);
      else if (bulkEditField === 'work_area_id') updates.work_area_id = bulkEditValue;
      else if (bulkEditField === 'task_estimated_effort') updates.task_estimated_effort = parseInt(bulkEditValue);
      else if (bulkEditField === 'task_delivery_date') updates.task_delivery_date = bulkEditValue;
      else if (bulkEditField === 'task_is_meeting') updates.task_is_meeting = bulkEditValue === 'true';

      const response = await fetch(`${API}/tasks/bulk?${params}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ task_ids: Array.from(selectedTasks), updates })
      });

      if (response.ok) {
        const result = await response.json();
        toast.success(`Updated ${result.modified_count} tasks`);
        setShowBulkEdit(false);
        setSelectedTasks(new Set());
        setBulkEditField('');
        setBulkEditValue('');
        fetchTasks();
      }
    } catch {
      toast.error('Bulk update failed');
    }
  };

  // Delete selected tasks
  const deleteSelectedTasks = async () => {
    if (selectedTasks.size === 0) {
      toast.error('No tasks selected');
      return;
    }
    
    setDeleting(true);
    try {
      const params = new URLSearchParams();
      if (actingAs) params.append('acting_as', actingAs);
      
      let deletedCount = 0;
      for (const taskId of selectedTasks) {
        const response = await fetch(`${API}/tasks/${taskId}?${params}`, {
          method: 'DELETE',
          credentials: 'include'
        });
        if (response.ok) deletedCount++;
      }
      
      toast.success(`Deleted ${deletedCount} tasks`);
      setSelectedTasks(new Set());
      fetchTasks();
    } catch {
      toast.error('Failed to delete tasks');
    } finally {
      setDeleting(false);
    }
  };

  // Soft delete all data (marks as deleted but keeps records)
  const deleteAllData = async () => {
    if (deleteConfirmText !== 'DELETE ALL') return;
    
    setDeleting(true);
    try {
      const params = new URLSearchParams();
      if (actingAs) params.append('acting_as', actingAs);
      
      const response = await fetch(`${API}/tasks/delete-all?${params}`, {
        method: 'DELETE',
        credentials: 'include'
      });

      if (response.ok) {
        const result = await response.json();
        toast.success(`Marked ${result.deleted_count} tasks as deleted`);
        setShowDeleteAllModal(false);
        setDeleteConfirmText('');
        fetchTasks();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to delete all data');
      }
    } catch {
      toast.error('Failed to delete data');
    } finally {
      setDeleting(false);
    }
  };

  // PURGE all data (permanent deletion, no recovery)
  const purgeAllData = async () => {
    if (deleteConfirmText !== 'PURGE ALL DATA') return;
    if (!showPurgeConfirm) {
      setShowPurgeConfirm(true);
      return;
    }
    
    setDeleting(true);
    try {
      const params = new URLSearchParams();
      if (actingAs) params.append('acting_as', actingAs);
      
      // Try POST first (more reliable through production proxies)
      let response;
      try {
        response = await fetch(`${API}/tasks/purge?${params}`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({ confirm: 'PURGE_ALL_DATA', acting_as: actingAs || undefined })
        });
      } catch {
        // Fall back to DELETE if POST fails
        response = null;
      }
      
      // If POST didn't work (404 or network error), try DELETE
      if (!response || response.status === 404 || response.status === 405) {
        params.append('confirm', 'PURGE_ALL_DATA');
        response = await fetch(`${API}/tasks/purge?${params}`, {
          method: 'DELETE',
          credentials: 'include'
        });
      }

      if (response.ok) {
        const result = await response.json();
        toast.success(`Permanently deleted ${result.tasks_deleted} tasks and ${result.mappings_deleted} mappings`);
        setShowDeleteAllModal(false);
        setDeleteConfirmText('');
        setShowPurgeConfirm(false);
        setDeleteMode('soft');
        setAllTasks([]);
        setTotalTaskCount(0);
      } else {
        const error = await response.json().catch(() => ({ detail: `Server error ${response.status}` }));
        toast.error(error.detail || 'Failed to purge data');
      }
    } catch {
      toast.error('Failed to purge data');
    } finally {
      setDeleting(false);
    }
  };

  // Export
  // CSV escape - quote ALL fields for maximum compatibility (RFC 4180)
  const csvEsc = (v) => `"${String(v ?? '').replace(/"/g, '""')}"`;

  // The canonical list of export/import fields and their task object keys
  const EXPORT_FIELDS = [
    { header: 'task_title', key: 'task_title' },
    { header: 'task_description', key: 'task_description' },
    { header: 'task_status', key: 'task_status' },
    { header: 'task_priority', key: 'task_priority' },
    { header: 'task_delivery_date', key: 'task_delivery_date' },
    { header: 'task_start_time', key: 'task_start_time' },
    { header: 'task_end_time', key: 'task_end_time' },
    { header: 'work_area_id', key: 'work_area_id' },
    { header: 'task_estimated_effort', key: 'task_estimated_effort' },
    { header: 'task_actual_effort', key: 'task_actual_effort' },
    { header: 'task_actual_personal_effort', key: 'task_actual_personal_effort' },
    { header: 'task_is_meeting', key: 'task_is_meeting' },
    { header: 'task_hidden', key: 'task_hidden' },
    { header: 'next_steps', key: 'next_steps' },
    { header: 'big_picture_id', key: 'big_picture_id' },
    { header: 'impact', key: 'impact' },
    { header: 'responsible_person', key: 'responsible_person_name' },
    { header: 'observers', key: 'observers' },
    { header: 'created_by', key: 'created_by_display' },
    { header: 'blocked_reason', key: 'blocked_reason' },
    { header: 'task_related_links', key: 'task_related_links' },
    { header: 'task_sequence', key: 'task_sequence' },
    { header: 'task_reschedule_count', key: 'task_reschedule_count' },
  ];

  const taskToRow = (t) => ({
    task_title: t.task_title || '',
    task_description: t.task_description || '',
    task_status: t.task_status || 'To Do',
    task_priority: t.task_priority ?? 3,
    task_delivery_date: t.task_local_date || t.task_delivery_date || '',
    task_start_time: t.task_local_start_time || t.task_start_time || '',
    task_end_time: t.task_local_end_time || t.task_end_time || '',
    work_area_id: t.work_area_id || 'General',
    task_estimated_effort: t.task_estimated_effort ?? 1,
    task_actual_effort: t.task_actual_effort ?? '',
    task_actual_personal_effort: t.task_actual_personal_effort ?? '',
    task_is_meeting: t.task_is_meeting ? 'true' : 'false',
    task_hidden: t.task_hidden ? 'true' : 'false',
    next_steps: t.next_steps || '',
    big_picture_id: t.big_picture_id || '',
    impact: t.impact || '',
    responsible_person_name: t.responsible_person_name || '',
    observers: (t.observers || []).join(', '),
    created_by_display: t.created_by_display || '',
    blocked_reason: t.blocked_reason || '',
    task_related_links: t.task_related_links || '',
    task_sequence: t.task_sequence || '',
    task_reschedule_count: t.task_reschedule_count ?? 0,
  });

  const exportTasks = (fmt = 'csv') => {
    const taskRows = filteredTasks.map(taskToRow);

    if (fmt === 'json') {
      const blob = new Blob([JSON.stringify({ format: 'orka_v1', tasks: taskRows, count: taskRows.length, exported_at: new Date().toISOString() }, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `orka_tasks_${format(new Date(), 'yyyy-MM-dd')}.json`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success(`Exported ${taskRows.length} tasks as JSON`);
      return;
    }

    // CSV: headers + every field quoted
    const headers = EXPORT_FIELDS.map(f => f.header);
    const rows = taskRows.map(row => headers.map(h => csvEsc(row[h])).join(','));
    const csv = [headers.join(','), ...rows].join('\r\n');
    const blob = new Blob(['\uFEFF' + csv], { type: 'text/csv;charset=utf-8' }); // BOM for Excel compat
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `orka_tasks_${format(new Date(), 'yyyy-MM-dd')}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success(`Exported ${taskRows.length} tasks as CSV`);
  };

  // ===== IMPORT =====
  // Detects format, sends directly to the import/json endpoint in chunks.
  // Both CSV and JSON are parsed client-side into the same task array, then sent.
  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    e.target.value = '';

    setImportProgress('Reading file...');
    setShowImportModal(true);
    setImportErrors([]);
    setImportStep(0);

    try {
      const text = await file.text();

      if (file.name.endsWith('.json')) {
        // ---- JSON path ---- skip mapping, go straight to preview
        const data = JSON.parse(text);
        const tasks = Array.isArray(data.tasks) ? data.tasks : Array.isArray(data) ? data : [];
        if (!tasks.length) {
          toast.error('No tasks found in JSON file');
          resetImportState();
          return;
        }
        setImportFileKind('json');
        setImportFileText(null);
        setImportData(tasks.slice(0, 10));
        setImportRowCount(tasks.length);
        importTasksRef.current = tasks;
        setImportStep(2);
        setImportProgress(null);
      } else {
        // ---- CSV path: parse headers + detect mapping, show mapping review step ----
        const { headers, detectedMapping, tasks, validation, rowMismatch } = parseCSVToTasks(text);
        if (!headers.length) {
          toast.error('No valid rows found in CSV');
          resetImportState();
          return;
        }
        setImportFileKind('csv');
        setImportFileText(text);
        setImportHeaders(headers);
        setColumnMapping(detectedMapping);
        setMappingValidation(validation || {});
        setRowMismatch(rowMismatch || { count: 0, samples: [] });
        setImportData(tasks.slice(0, 10));
        setImportRowCount(tasks.length);
        importTasksRef.current = tasks;
        setImportStep(1); // Start at mapping review step
        setImportProgress(null);
      }
    } catch (err) {
      console.error('File read error:', err);
      toast.error(`Failed to read file: ${err.message}`);
      resetImportState();
    }
  };

  // Apply the current mapping and advance to preview
  const applyMappingAndPreview = () => {
    if (!importFileText) { setImportStep(2); return; }
    const { tasks, validation } = parseCSVToTasks(importFileText, columnMapping);
    setMappingValidation(validation || {});
    if (!tasks.length) {
      toast.error('Mapping yielded zero tasks. Make sure a column is mapped to "task_title".');
      return;
    }
    setImportData(tasks.slice(0, 10));
    setImportRowCount(tasks.length);
    importTasksRef.current = tasks;
    setImportStep(2);
  };

  // Update a single column mapping and re-validate against the current CSV.
  const updateColumnMapping = (colIdx, dbField) => {
    const nextMapping = { ...columnMapping };
    if (!dbField || dbField === '__skip__') {
      delete nextMapping[colIdx];
    } else {
      nextMapping[colIdx] = dbField;
    }
    setColumnMapping(nextMapping);
    if (importFileText) {
      // Re-run the validator so the UI immediately reflects type mismatches on
      // the chosen field (e.g. mapping a description column to task_delivery_date
      // will show "32 of 33 rows not a date" instantly).
      const { validation } = parseCSVToTasks(importFileText, nextMapping);
      setMappingValidation(validation || {});
    }
  };

  // Simple RFC 4180 CSV parser → { headers, detectedMapping, tasks }
  // `overrideMapping` lets the manual mapping step re-parse without re-reading the file.
  const parseCSVToTasks = (text, overrideMapping = null) => {
    // Strip BOM
    if (text.charCodeAt(0) === 0xFEFF) text = text.slice(1);
    // Normalize line endings
    text = text.replace(/\r\n/g, '\n').replace(/\r/g, '\n');

    const lines = [];
    let current = '';
    let inQuotes = false;
    for (let i = 0; i < text.length; i++) {
      const ch = text[i];
      if (ch === '"') {
        if (inQuotes && text[i + 1] === '"') {
          current += '"';
          i++;
        } else {
          inQuotes = !inQuotes;
        }
      } else if (ch === '\n' && !inQuotes) {
        lines.push(current);
        current = '';
      } else {
        current += ch;
      }
    }
    if (current.trim()) lines.push(current);

    if (lines.length < 2) return { headers: [], detectedMapping: {}, tasks: [], validation: {}, totalDataRows: 0, rowMismatch: { count: 0, samples: [] } };

    const splitRow = (line) => {
      const cells = [];
      let cell = '';
      let q = false;
      for (let i = 0; i < line.length; i++) {
        const ch = line[i];
        if (ch === '"') {
          if (q && line[i + 1] === '"') { cell += '"'; i++; }
          else q = !q;
        } else if (ch === ',' && !q) {
          cells.push(cell);
          cell = '';
        } else {
          cell += ch;
        }
      }
      cells.push(cell);
      return cells;
    };

    const rawHeaders = splitRow(lines[0]).map(h => h.trim());
    const normHeaders = rawHeaders.map(h => h.toLowerCase().replace(/[^a-z0-9_]/g, ''));

    // Build header → DB field auto-detect (canonical field names only).
    const detectedMapping = {};
    normHeaders.forEach((h, i) => {
      // Exact match against canonical export headers first.
      const exactMatch = EXPORT_FIELDS.find(f => f.header === h);
      if (exactMatch) { detectedMapping[i] = exactMatch.key; return; }
      // Legacy / fuzzy — ORDER MATTERS: more specific tokens before generic ones
      // so e.g. "task_delivery_date" doesn't steal the description column.
      if (h === 'title' || h === 'name' || h === 'task' || h === 'task_title')
        detectedMapping[i] = 'task_title';
      else if (h === 'description' || h === 'task_description' || h === 'notes' || h === 'note' || h.startsWith('description') || h.startsWith('task_description'))
        detectedMapping[i] = 'task_description';
      else if (h === 'task_status_key' || h === 'status_key' || h === 'task_status' || h === 'status' || h === 'state')
        detectedMapping[i] = 'task_status_key';
      else if (h === 'task_priority' || h === 'priority' || h === 'p' || h.startsWith('priority'))
        detectedMapping[i] = 'task_priority';
      else if (h === 'task_local_date' || h === 'task_delivery_date' || h === 'delivery_date' || h === 'duedate' || h === 'due_date' || h === 'date')
        detectedMapping[i] = 'task_local_date';
      else if (h === 'task_local_start_time' || h === 'task_start_time' || h === 'start_time' || h === 'starttime' || h === 'start')
        detectedMapping[i] = 'task_local_start_time';
      else if (h === 'task_local_end_time' || h === 'task_end_time' || h === 'end_time' || h === 'endtime' || h === 'end')
        detectedMapping[i] = 'task_local_end_time';
      else if (h === 'task_local_timezone_context' || h === 'timezone' || h === 'tz')
        detectedMapping[i] = 'task_local_timezone_context';
      else if (h === 'work_area_id' || h === 'work_area' || h === 'workarea' || h === 'area' || h === 'category')
        detectedMapping[i] = 'work_area_id';
      else if (h === 'personal_sub_area_id' || h === 'personal_sub_area' || h === 'sub_area')
        detectedMapping[i] = 'personal_sub_area_id';
      else if (h === 'task_actual_personal_effort' || h === 'actual_personal_effort' || h === 'actualpersonaleffort')
        detectedMapping[i] = 'task_actual_personal_effort';
      else if (h === 'task_actual_effort' || h === 'actual_effort' || h === 'actualeffort')
        detectedMapping[i] = 'task_actual_effort';
      else if (h === 'task_estimated_effort' || h === 'estimated_effort' || h === 'effort' || h === 'estimate' || h === 'points' || h === 'est_effort')
        detectedMapping[i] = 'task_estimated_effort';
      else if (h === 'task_is_meeting' || h === 'is_meeting' || h === 'meeting')
        detectedMapping[i] = 'task_is_meeting';
      else if (h === 'task_hidden' || h === 'hidden' || h === 'suppressed' || h === 'is_suppressed')
        detectedMapping[i] = 'task_hidden';
      else if (h === 'next_steps' || h === 'nextsteps')
        detectedMapping[i] = 'next_steps';
      else if (h === 'big_picture_id' || h === 'epic')
        detectedMapping[i] = 'big_picture_id';
      else if (h === 'impact')
        detectedMapping[i] = 'impact';
      else if (h === 'responsible_person_id' || h === 'responsible_person' || h === 'responsible_person_name' || h === 'person_responsible' || h === 'assignee')
        detectedMapping[i] = 'responsible_person_id';
      else if (h === 'observers' || h === 'observer_ids')
        detectedMapping[i] = 'observers';
      else if (h === 'blocked_reason')
        detectedMapping[i] = 'blocked_reason';
      else if (h === 'task_related_links' || h === 'related_links' || h === 'links')
        detectedMapping[i] = 'task_related_links';
      else if (h === 'task_sequence' || h === 'sequence' || h === 'sequencing')
        detectedMapping[i] = 'task_sequence';
      else if (h === 'task_reschedule_count' || h === 'reschedule_count')
        detectedMapping[i] = 'task_reschedule_count';
      // Removed/derived legacy fields are silently ignored on import:
      //   created_by_display, task_estimated_minutes, task_duration_minutes,
      //   responsible_person_name (use responsible_person_id), task_associated_person_id (use observers).
      // Everything else → unmapped (user can set via the mapping step)
    });

    const activeMapping = overrideMapping || detectedMapping;

    // Build a type lookup for quick validation during parsing.
    const fieldTypeByKey = Object.fromEntries(DB_FIELDS.map(f => [f.key, f.type]));

    const tasks = [];
    const perColumnInvalid = {}; // colIdx -> count
    const perColumnSamples = {}; // colIdx -> [{row, value, error}]
    let totalDataRows = 0;
    const rowMismatchSamples = [];
    let rowMismatchCount = 0;
    const expectedCols = rawHeaders.length;

    for (let i = 1; i < lines.length; i++) {
      const cells = splitRow(lines[i]);
      if (!cells.some(c => c.trim())) continue;

      // Column-count guardrail — if a row has a different number of cells than the
      // header, its values WILL be misaligned (column-shift). Skip it, record the issue.
      if (expectedCols > 0 && cells.length !== expectedCols) {
        rowMismatchCount++;
        if (rowMismatchSamples.length < 5) {
          rowMismatchSamples.push({
            row: i + 1,
            got: cells.length,
            expected: expectedCols,
            preview: lines[i].slice(0, 140),
          });
        }
        continue;
      }

      totalDataRows++;

      const task = {};
      for (const [colIdxStr, field] of Object.entries(activeMapping)) {
        if (!field) continue;
        const colIdx = parseInt(colIdxStr);
        const raw = (cells[colIdx] || '').trim();
        if (!raw) continue;
        const type = fieldTypeByKey[field] || 'text';
        const err = validateImportValue(type, raw);
        if (err) {
          // Type mismatch — DO NOT write to this field. This is the guardrail that
          // prevents "Description → task_delivery_date" leaks no matter what the
          // user (or the auto-detector) mapped.
          perColumnInvalid[colIdx] = (perColumnInvalid[colIdx] || 0) + 1;
          if ((perColumnSamples[colIdx] || []).length < 3) {
            perColumnSamples[colIdx] = [...(perColumnSamples[colIdx] || []), { row: i + 1, value: raw, error: err }];
          }
          continue;
        }
        task[field] = normalizeImportValue(type, raw);
      }
      if (!task.task_title) continue;
      tasks.push(task);
    }

    // Summarise invalid counts per column — the mapping UI will surface these.
    const validation = {};
    for (const [colIdxStr, count] of Object.entries(perColumnInvalid)) {
      const colIdx = parseInt(colIdxStr);
      const field = activeMapping[colIdx];
      if (!field) continue;
      validation[colIdx] = {
        field,
        invalid: count,
        total: totalDataRows,
        samples: perColumnSamples[colIdx] || [],
      };
    }

    return { headers: rawHeaders, detectedMapping, tasks, validation, totalDataRows, rowMismatch: { count: rowMismatchCount, samples: rowMismatchSamples } };
  };

  // Execute import: send tasks in chunks, with retry and fallback
  const executeImport = async () => {
    const tasks = importTasksRef.current;
    if (!tasks?.length) return;

    setImporting(true);
    setImportProgress(`Importing 0 of ${tasks.length}...`);

    const CHUNK = 25; // Smaller chunks for maximum reliability through proxies
    const MAX_RETRIES = 2;
    let imported = 0;
    let errors = [];
    let useJsonEndpoint = true; // Try new endpoint first

    try {
      const params = new URLSearchParams();
      if (actingAs) params.append('acting_as', actingAs);

      for (let i = 0; i < tasks.length; i += CHUNK) {
        const chunk = tasks.slice(i, i + CHUNK);
        const chunkNum = Math.floor(i / CHUNK) + 1;
        const totalChunks = Math.ceil(tasks.length / CHUNK);
        setImportProgress(`Importing batch ${chunkNum} of ${totalChunks} (${Math.min(i + CHUNK, tasks.length)} of ${tasks.length} tasks)...`);

        let response = null;
        let lastErr = null;

        // Retry loop for each chunk
        for (let attempt = 0; attempt <= MAX_RETRIES; attempt++) {
          let endpoint = useJsonEndpoint ? `${API}/tasks/import/json` : `${API}/tasks/import/csv`;

          try {
            response = await fetch(`${endpoint}?${params}`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              credentials: 'include',
              body: JSON.stringify({ tasks: chunk })
            });
          } catch (networkErr) {
            lastErr = `Network error - ${networkErr.message}`;
            // Wait before retry with exponential backoff
            if (attempt < MAX_RETRIES) {
              await new Promise(r => setTimeout(r, 1000 * (attempt + 1)));
              continue;
            }
            errors.push(`Batch ${chunkNum}: ${lastErr}`);
            response = null;
            break;
          }

          if (!response.ok) {
            const statusCode = response.status;
            const errBody = await response.text().catch(() => '');
            let errMsg = '';
            try { errMsg = JSON.parse(errBody).detail || errBody; } catch { errMsg = errBody; }

            // If 404 on json endpoint, switch to csv endpoint
            if (statusCode === 404 && useJsonEndpoint) {
              useJsonEndpoint = false;
              continue; // Retry immediately with csv endpoint
            }

            lastErr = `(${statusCode}): ${errMsg.slice(0, 200)}`;

            // Retry on 500/502/503/504 (server errors)
            if (statusCode >= 500 && attempt < MAX_RETRIES) {
              await new Promise(r => setTimeout(r, 1000 * (attempt + 1)));
              continue;
            }

            errors.push(`Batch ${chunkNum} ${lastErr}`);
            response = null;
            break;
          }

          // Success - break retry loop
          break;
        }

        if (response && response.ok) {
          try {
            const result = await response.json();
            imported += result.imported_count || 0;
            if (result.errors?.length) {
              result.errors.forEach(e => errors.push(e));
            }
          } catch (parseErr) {
            errors.push(`Batch ${chunkNum}: Response parse error`);
          }
        }
      }

      setImportProgress(null);

      if (imported > 0) {
        toast.success(`Imported ${imported} of ${tasks.length} tasks`);
        fetchTasks();
      }

      if (errors.length > 0) {
        // Show first error detail in toast
        const firstErr = errors[0];
        toast.warning(`${errors.length} errors. First: ${firstErr.slice(0, 100)}`);
        setImportErrors(errors);
        // Keep modal open so user can see errors
        setImporting(false);
        setImportStep(3); // Error review step
        return;
      }

      resetImportState();
    } catch (err) {
      toast.error(`Import failed: ${err.message}`);
    } finally {
      setImporting(false);
    }
  };

  const resetImportState = () => {
    setShowImportModal(false);
    setImportStep(1);
    setImportStagingId(null);
    setImportRowCount(0);
    setImportSampleRow(null);
    setImportHeaders([]);
    setColumnMapping({});
    setMappingValidation({});
    setRowMismatch({ count: 0, samples: [] });
    setImportFileText(null);
    setImportFileKind(null);
    setImportData([]);
    setImportErrors([]);
    setImportProgress(null);
  };

  // Format date for display
  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    try {
      return format(parseISO(dateStr), 'd MMM yy');
    } catch {
      return dateStr;
    }
  };

  // Get priority option from PRIORITY_META
  const getPriorityOption = (value) => {
    const meta = PRIORITY_META[String(value)];
    return meta || PRIORITY_META["3"];
  };

  // Header actions for PageLayout
  const headerActions = (
    <div className="flex items-center gap-2">
      {/* Task count info */}
      <span className="text-sm text-muted-foreground mr-2">
        {hasFilters 
          ? `${filteredTasks.length} of ${totalTaskCount}`
          : `${displayedTasks.length} of ${totalTaskCount}`
        }
        {selectedTasks.size > 0 && ` | ${selectedTasks.size} selected`}
        {pendingEditCount > 0 && <span className="text-amber-600"> | {pendingEditCount} unsaved</span>}
      </span>

      {/* Save All Button */}
      {pendingEditCount > 0 && (
        <Button
          className="h-9 px-3 rounded-full bg-emerald-600 text-white hover:bg-emerald-700"
          onClick={saveAllChanges}
          data-testid="save-all-changes-button"
        >
          <Save className="h-4 w-4 mr-1" />
          Save ({pendingEditCount})
        </Button>
      )}

      {/* Bulk Edit Button */}
      {selectedTasks.size > 0 && (
        <>
          <Button
            className="h-9 px-3 rounded-full bg-blue-600 text-white hover:bg-blue-700"
            onClick={() => setShowBulkEdit(true)}
            data-testid="bulk-edit-button"
          >
            <Edit2 className="h-4 w-4 mr-1" />
            Edit ({selectedTasks.size})
          </Button>
          <Button
            variant="destructive"
            className="h-9 px-3 rounded-full"
            onClick={deleteSelectedTasks}
            disabled={deleting}
            data-testid="delete-selected-button"
          >
            <Trash2 className="h-4 w-4 mr-1" />
            Delete
          </Button>
        </>
      )}

      {/* Export Button - Dropdown with CSV and JSON */}
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="outline" className="h-9 px-3 rounded-full" data-testid="export-button">
            <Download className="h-4 w-4 mr-1" />Export
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          <DropdownMenuItem onClick={() => exportTasks('csv')} data-testid="export-csv">
            <FileText className="w-4 h-4 mr-2" />Export as CSV
          </DropdownMenuItem>
          <DropdownMenuItem onClick={() => exportTasks('json')} data-testid="export-json">
            <Database className="w-4 h-4 mr-2" />Export as JSON (recommended for migration)
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>

      {/* Import Button */}
      <Button
        variant="outline"
        className="h-9 px-3 rounded-full"
        onClick={() => setShowImportDialog(true)}
        data-testid="import-button"
      >
        <Upload className="h-4 w-4 mr-1" />
        Import
      </Button>
      <input ref={fileInputRef} type="file" accept=".csv,.json" className="hidden" onChange={handleFileUpload} />

      {/* Filters Toggle */}
      <Button
        variant={showFilters || activeFilterCount > 0 ? 'default' : 'outline'}
        className={`h-9 px-3 rounded-full ${showFilters || activeFilterCount > 0 ? 'bg-blue-600 text-white hover:bg-blue-700' : ''}`}
        onClick={() => setShowFilters(!showFilters)}
        data-testid="filters-toggle"
      >
        <Filter className="h-4 w-4 mr-1" />
        Filter
        {activeFilterCount > 0 && (
          <Badge className="ml-1 orka-surface-raised text-blue-600 text-xs px-1.5 py-0.5 rounded-full font-bold">
            {activeFilterCount}
          </Badge>
        )}
      </Button>

      {/* Delete All Button */}
      <Button
        variant="outline"
        className="h-9 px-3 rounded-full border-red-200 text-red-600 hover:bg-red-50"
        onClick={() => setShowDeleteAllModal(true)}
        data-testid="delete-all-button"
      >
        <Database className="h-4 w-4 mr-1" />
        Purge
      </Button>
    </div>
  );

  // Allow embedding inside a tab — render a Fragment instead of PageLayout
  const Wrapper = embedded ? React.Fragment : PageLayout;
  const wrapperProps = embedded ? {} : { title: "Tasks", user, onLogout: logout, theme, setTheme, actions: headerActions };

  return (
    <Wrapper {...wrapperProps}>
      {embedded && (
        <div className="flex items-center justify-end gap-2 mb-3">{headerActions}</div>
      )}
      {/* Filter Panel - appears above table when filters are toggled */}
      {showFilters && (
        <div className="orka-surface-raised rounded-xl border border orka-border-subtle p-4 mb-4 shadow-sm">
          <div className="flex items-center justify-between mb-3">
            <span className="text-sm font-semibold orka-text-secondary">Filters</span>
            {activeFilterCount > 0 && (
              <button
                onClick={clearAllFilters}
                className="text-xs text-red-600 hover:text-red-700"
              >
                Clear all
              </button>
            )}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {/* Search */}
            <div>
              <label className="text-xs font-medium orka-text-secondary dark:text-slate-500 mb-1 block">Search</label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                <Input
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Search tasks..."
                  className="h-9 pl-9 pr-3 rounded-lg border orka-border-subtle text-sm"
                  data-testid="search-input"
                />
              </div>
            </div>

            {/* Date Range */}
            <div>
              <label className="text-xs font-medium orka-text-secondary dark:text-slate-500 mb-1 block">Date Range</label>
              <div className="space-y-2">
                <div className="flex gap-2">
                  <Input
                    type="date"
                    value={dateFrom}
                    onChange={(e) => setDateFrom(e.target.value)}
                    className="h-9 px-2 rounded-lg border orka-border-subtle text-sm flex-1"
                    disabled={dateIsEmpty}
                  />
                  <Input
                    type="date"
                    value={dateTo}
                    onChange={(e) => setDateTo(e.target.value)}
                    className="h-9 px-2 rounded-lg border orka-border-subtle text-sm flex-1"
                    disabled={dateIsEmpty}
                  />
                </div>
                <div className="flex items-center gap-2">
                  <Checkbox 
                    id="dateIsEmpty" 
                    checked={dateIsEmpty} 
                    onCheckedChange={(checked) => {
                      setDateIsEmpty(checked);
                      if (checked) {
                        setDateFrom('');
                        setDateTo('');
                      }
                    }}
                    data-testid="date-is-empty-checkbox"
                  />
                  <label htmlFor="dateIsEmpty" className="text-xs orka-text-secondary dark:text-slate-500 cursor-pointer">
                    Date is Empty (Backlog tasks)
                  </label>
                </div>
              </div>
            </div>

            {/* Status Filter */}
            <div>
              <label className="text-xs font-medium orka-text-secondary dark:text-slate-500 mb-1 block">Status</label>
              <div className="flex flex-wrap gap-1">
                {STATUS_OPTIONS.map(status => (
                  <button
                    key={status}
                    onClick={() => toggleStatusFilter(status)}
                    className={`text-xs px-2 py-1 rounded-full transition-colors ${
                      statusFilters.has(status) 
                        ? 'bg-blue-600 text-white' 
                        : 'orka-surface-subtle orka-text-secondary dark:text-slate-500 hover:bg-stone-200 dark:hover:bg-slate-700'
                    }`}
                  >
                    {status}
                  </button>
                ))}
              </div>
            </div>

            {/* Priority Filter */}
            <div>
              <label className="text-xs font-medium orka-text-secondary dark:text-slate-500 mb-1 block">Priority</label>
              <div className="flex flex-wrap gap-1">
                {PRIORITY_OPTIONS.map(p => (
                  <button
                    key={p.value}
                    onClick={() => togglePriorityFilter(p.value)}
                    className={`text-xs px-3 py-1 rounded-full font-bold transition-colors ${
                      priorityFilters.has(p.value) 
                        ? 'bg-blue-600 text-white' 
                        : 'orka-surface-subtle orka-text-secondary dark:text-slate-500 hover:bg-stone-200 dark:hover:bg-slate-700'
                    }`}
                  >
                    {p.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Work Area Filter */}
            <div className="md:col-span-2">
              <label className="text-xs font-medium orka-text-secondary dark:text-slate-500 mb-1 block">Work Area</label>
              <div className="flex flex-wrap gap-1 mb-2">
                {uniqueWorkAreas.map(area => (
                  <button
                    key={area}
                    onClick={() => toggleWorkAreaFilter(area)}
                    disabled={workAreaIsEmpty}
                    className={`text-xs px-2 py-1 rounded-full transition-colors ${
                      workAreaFilters.has(area) 
                        ? 'bg-blue-600 text-white' 
                        : 'orka-surface-subtle orka-text-secondary dark:text-slate-500 hover:bg-stone-200 dark:hover:bg-slate-700'
                    } ${workAreaIsEmpty ? 'opacity-50 cursor-not-allowed' : ''}`}
                  >
                    {area}
                  </button>
                ))}
              </div>
              <div className="flex items-center gap-2">
                <Checkbox 
                  id="workAreaIsEmpty" 
                  checked={workAreaIsEmpty} 
                  onCheckedChange={(checked) => {
                    setWorkAreaIsEmpty(checked);
                    if (checked) {
                      setWorkAreaFilters(new Set());
                    }
                  }}
                  data-testid="work-area-is-empty-checkbox"
                />
                <label htmlFor="workAreaIsEmpty" className="text-xs orka-text-secondary dark:text-slate-500 cursor-pointer">
                  Empty (no work area assigned)
                </label>
              </div>
            </div>
          </div>
          
          {/* Filter Now Button */}
          <div className="flex items-center justify-end gap-3 mt-4 pt-4 border-t border orka-border-subtle">
            <Button
              variant="outline"
              size="sm"
              onClick={() => {
                setDateFrom('');
                setDateTo('');
                setDateIsEmpty(false);
                setStatusFilters(new Set());
                setPriorityFilters(new Set());
                setWorkAreaFilters(new Set());
                setWorkAreaIsEmpty(false);
                setFilterApplied(false);
              }}
              className="h-8"
            >
              Reset Filters
            </Button>
            <Button
              size="sm"
              onClick={() => setFilterApplied(true)}
              className="h-8 bg-blue-600 hover:bg-blue-700"
              data-testid="filter-now-btn"
            >
              <Filter className="w-3.5 h-3.5 mr-1.5" />
              Filter Now
            </Button>
          </div>
        </div>
      )}

      {/* Data Table */}
      <div className="orka-surface-raised rounded-xl border border orka-border-subtle shadow-sm overflow-hidden">
        <div className="overflow-auto" style={{ minWidth: '900px' }}>
          <table className="w-full">
            {/* Table Header */}
            <thead className="sticky top-0 z-10 orka-surface-subtle">
              <tr>
                <th className="text-left p-2 text-xs font-semibold orka-text-secondary dark:text-slate-500 border-b border orka-border-subtle w-10">
                  <button onClick={toggleSelectAll} className="p-1">
                    {selectedTasks.size === 0 ? (
                      <Square className="w-4 h-4 orka-text-tertiary hover:orka-text-secondary" />
                    ) : selectedTasks.size === displayedTasks.length ? (
                      <CheckSquare className="w-4 h-4 text-blue-600" />
                    ) : (
                      <Minus className="w-4 h-4 orka-text-tertiary" />
                    )}
                  </button>
                </th>
                <th className="text-left p-2 text-xs font-semibold orka-text-secondary dark:text-slate-500 border-b border orka-border-subtle w-10">
                  <Pencil className="w-3 h-3" />
                </th>
                <th 
                  className="text-left p-2 text-xs font-semibold orka-text-secondary dark:text-slate-500 border-b border orka-border-subtle cursor-pointer hover:bg-stone-200 dark:hover:bg-slate-700 w-28"
                  onClick={() => toggleSort('task_delivery_date')}
                >
                  <div className="flex items-center gap-1">
                    Date
                    {sortField === 'task_delivery_date' && (
                      sortDirection === 'asc' ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />
                    )}
                  </div>
                </th>
                <th 
                  className="text-left p-2 text-xs font-semibold orka-text-secondary dark:text-slate-500 border-b border orka-border-subtle cursor-pointer hover:bg-stone-200 dark:hover:bg-slate-700 w-[70px]"
                  onClick={() => toggleSort('task_priority')}
                >
                  <div className="flex items-center gap-1">
                    Priority
                    {sortField === 'task_priority' && (
                      sortDirection === 'asc' ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />
                    )}
                  </div>
                </th>
                <th 
                  className="text-left p-2 text-xs font-semibold orka-text-secondary dark:text-slate-500 border-b border orka-border-subtle cursor-pointer hover:bg-stone-200 dark:hover:bg-slate-700 w-[130px]"
                  onClick={() => toggleSort('work_area_id')}
                >
                  <div className="flex items-center gap-1">
                    Work Area
                    {sortField === 'work_area_id' && (
                      sortDirection === 'asc' ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />
                    )}
                    <button
                      onClick={(e) => { e.stopPropagation(); setShowWorkAreaEditor(true); }}
                      className="ml-auto p-0.5 rounded hover:bg-stone-300 transition-colors"
                      title="Manage work areas"
                      data-testid="work-area-edit-btn"
                    >
                      <Pencil className="w-3 h-3 orka-text-tertiary" />
                    </button>
                  </div>
                </th>
                <th className="text-left p-2 text-xs font-semibold orka-text-secondary dark:text-slate-500 border-b border orka-border-subtle w-[100px]">
                  Sub Area
                </th>
                <th 
                  className="text-left p-2 text-xs font-semibold orka-text-secondary dark:text-slate-500 border-b border orka-border-subtle cursor-pointer hover:bg-stone-200 dark:hover:bg-slate-700 w-60"
                  onClick={() => toggleSort('title')}
                >
                  <div className="flex items-center gap-1">
                    Title
                    {sortField === 'title' && (
                      sortDirection === 'asc' ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />
                    )}
                  </div>
                </th>
                <th 
                  className="text-left p-2 text-xs font-semibold orka-text-secondary dark:text-slate-500 border-b border orka-border-subtle cursor-pointer hover:bg-stone-200 dark:hover:bg-slate-700 w-[70px]"
                  onClick={() => toggleSort('task_estimated_effort')}
                >
                  <div className="flex items-center gap-1">
                    Est.
                    {sortField === 'task_estimated_effort' && (
                      sortDirection === 'asc' ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />
                    )}
                  </div>
                </th>
                <th 
                  className="text-left p-2 text-xs font-semibold orka-text-secondary dark:text-slate-500 border-b border orka-border-subtle cursor-pointer hover:bg-stone-200 dark:hover:bg-slate-700 w-[70px]"
                  onClick={() => toggleSort('task_actual_effort')}
                >
                  <div className="flex items-center gap-1">
                    Actual
                    {sortField === 'task_actual_effort' && (
                      sortDirection === 'asc' ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />
                    )}
                  </div>
                </th>
                <th 
                  className="text-left p-2 text-xs font-semibold orka-text-secondary dark:text-slate-500 border-b border orka-border-subtle cursor-pointer hover:bg-stone-200 dark:hover:bg-slate-700 w-[70px]"
                  onClick={() => toggleSort('task_actual_personal_effort')}
                >
                  <div className="flex items-center gap-1">
                    Personal
                    {sortField === 'task_actual_personal_effort' && (
                      sortDirection === 'asc' ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />
                    )}
                  </div>
                </th>
                <th 
                  className="text-left p-2 text-xs font-semibold orka-text-secondary dark:text-slate-500 border-b border orka-border-subtle cursor-pointer hover:bg-stone-200 dark:hover:bg-slate-700 w-[140px]"
                  onClick={() => toggleSort('task_status')}
                >
                  <div className="flex items-center gap-1">
                    Status
                    {sortField === 'task_status' && (
                      sortDirection === 'asc' ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />
                    )}
                  </div>
                </th>
                <th className="text-left p-2 text-xs font-semibold orka-text-secondary dark:text-slate-500 border-b border orka-border-subtle w-[70px]">
                  Meeting
                </th>
                <th 
                  className="text-left p-2 text-xs font-semibold orka-text-secondary dark:text-slate-500 border-b border orka-border-subtle cursor-pointer hover:bg-stone-200 dark:hover:bg-slate-700 w-[60px]"
                  onClick={() => toggleSort('task_hidden')}
                >
                  <div className="flex items-center gap-1">
                    Hide
                    {sortField === 'task_hidden' && (
                      sortDirection === 'asc' ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />
                    )}
                  </div>
                </th>
              </tr>
            </thead>

            {/* Table Body */}
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={12} className="p-8 text-center">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-slate-900 mx-auto" />
                  </td>
                </tr>
              ) : displayedTasks.length === 0 ? (
                <tr>
                  <td colSpan={12} className="text-center py-12 text-slate-400">
                    <Database className="w-12 h-12 mx-auto mb-4 opacity-50" />
                    <p>{hasFilters ? 'No tasks match your filters' : 'No tasks found'}</p>
                  </td>
                </tr>
              ) : (
                displayedTasks.map((task) => (
                  <tr 
                    key={task.task_id}
                    className={`border-b border-stone-100 dark:border-slate-700 hover:bg-stone-50 dark:hover:bg-slate-800 dark:hover:bg-slate-200 dark:bg-slate-800 transition-colors ${
                      selectedTasks.has(task.task_id) ? 'bg-blue-50' : ''
                    }`}
                    data-testid={`data-row-${task.task_id}`}
                  >
                    {/* Select */}
                    <td className="p-2">
                      <button onClick={() => toggleTaskSelection(task.task_id)} className="p-1">
                        {selectedTasks.has(task.task_id) ? (
                          <CheckSquare className="w-4 h-4 text-blue-600" />
                        ) : (
                          <Square className="w-4 h-4 orka-text-tertiary hover:orka-text-secondary" />
                        )}
                      </button>
                    </td>

                    {/* Edit Button */}
                    <td className="p-2">
                      <button
                        onClick={() => openEditModal(task)}
                        className="p-1 rounded hover:bg-slate-200 orka-text-tertiary dark:text-slate-500 hover:text-slate-700 dark:hover:text-slate-300 dark:text-slate-300"
                        data-testid={`edit-task-${task.task_id}`}
                      >
                        <Pencil className="w-4 h-4" />
                      </button>
                    </td>

                    {/* Date */}
                    <td className="p-2">
                      <DatePickerWithStats
                        value={task.task_local_date || ''}
                        onChange={(val) => updateTaskField(task.task_id, 'task_delivery_date', val || null)}
                        triggerClassName="h-7 text-xs border-0 bg-transparent hover:bg-blue-50 dark:bg-blue-900/20 font-mono"
                      />
                    </td>

                    {/* Priority */}
                    <td className="p-2" data-testid={`priority-cell-${task.task_id}`}>
                      <Select 
                        value={String(task.task_priority)} 
                        onValueChange={(v) => updateTaskField(task.task_id, 'task_priority', parseInt(v))}
                      >
                        <SelectTrigger 
                          className="h-7 w-14 text-xs font-bold border-0 [&>svg]:hidden justify-center"
                          style={{ backgroundColor: getPriorityOption(task.task_priority).bg, color: getPriorityOption(task.task_priority).text }}
                        >
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {Object.entries(PRIORITY_META).map(([v, meta]) => (
                            <SelectItem key={v} value={v}>
                              <span className="px-2 py-0.5 rounded font-bold" style={{ backgroundColor: meta.bg, color: meta.text }}>{meta.label}</span>
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </td>

                    {/* Work Area */}
                    <td className="p-2" data-testid={`work-area-cell-${task.task_id}`}>
                      <Select 
                        value={task.work_area_id || 'General'} 
                        onValueChange={(v) => updateTaskField(task.task_id, 'work_area_id', v)}
                      >
                        <SelectTrigger 
                          className="h-7 w-28 text-xs border-0 font-medium"
                          style={{ backgroundColor: WORK_AREA_COLORS[task.work_area_id] || '#E5E7EB', color: getTextColorForBg(WORK_AREA_COLORS[task.work_area_id] || '#E5E7EB') }}
                        >
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {workAreaNames.map(w => (
                            <SelectItem key={w} value={w}>
                              <span 
                                className="px-2 py-0.5 rounded text-xs font-medium"
                                style={{ backgroundColor: WORK_AREA_COLORS[w] || '#E5E7EB', color: getTextColorForBg(WORK_AREA_COLORS[w] || '#E5E7EB') }}
                              >
                                {w}
                              </span>
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </td>

                    {/* Personal Sub Area */}
                    <td className="p-2">
                      {task.work_area_id === 'Personal' ? (
                        <span className="text-xs orka-text-secondary">{task.personal_sub_area || '-'}</span>
                      ) : (
                        <span className="text-xs text-slate-300">-</span>
                      )}
                    </td>

                    {/* Title */}
                    <td className="p-2">
                      {editingCell?.taskId === task.task_id && editingCell?.field === 'title' ? (
                        <div className="flex items-center gap-1">
                          <Input
                            value={editValue}
                            onChange={(e) => setEditValue(e.target.value)}
                            className="h-7 text-sm border-blue-400"
                            autoFocus
                            onKeyDown={(e) => e.key === 'Enter' && saveEdit() || e.key === 'Escape' && cancelEdit()}
                          />
                          <button onClick={saveEdit} className="p-1 rounded bg-emerald-500 hover:bg-emerald-600 text-white">
                            <Check className="w-3 h-3" />
                          </button>
                          <button onClick={cancelEdit} className="p-1 rounded bg-red-500 hover:bg-red-600 text-white">
                            <X className="w-3 h-3" />
                          </button>
                        </div>
                      ) : (
                        <span 
                          className={`text-sm orka-text-secondary truncate block max-w-60 cursor-pointer hover:bg-blue-50 dark:bg-blue-900/20 rounded px-1 ${
                            pendingEdits[task.task_id]?.task_title ? 'bg-amber-50 dark:bg-amber-900/20 ring-1 ring-amber-300' : ''
                          }`}
                          onClick={() => startEdit(task.task_id, 'title', task.task_title)}
                        >
                          {task.task_title}
                        </span>
                      )}
                    </td>

                    {/* Effort */}
                    <td className="p-2">
                      <Select 
                        value={String(task.task_estimated_effort || 1)} 
                        onValueChange={(v) => updateTaskField(task.task_id, 'task_estimated_effort', parseInt(v))}
                      >
                        <SelectTrigger className="h-7 w-14 text-sm font-bold orka-text-secondary text-center border orka-border-subtle">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {EFFORT_OPTIONS.map(e => (
                            <SelectItem key={e} value={String(e)}>{e}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </td>

                    {/* Actual Effort */}
                    <td className="p-2">
                      <Select 
                        value={task.task_actual_effort != null ? String(task.task_actual_effort) : '_none'} 
                        onValueChange={(v) => updateTaskField(task.task_id, 'task_actual_effort', v === '_none' ? null : parseInt(v))}
                      >
                        <SelectTrigger className="h-7 w-14 text-sm orka-text-secondary text-center border orka-border-subtle">
                          <SelectValue placeholder="-" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="_none">-</SelectItem>
                          {EFFORT_OPTIONS.map(e => (
                            <SelectItem key={e} value={String(e)}>{e}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </td>

                    {/* Actual Personal Effort */}
                    <td className="p-2">
                      <Select 
                        value={task.task_actual_personal_effort != null ? String(task.task_actual_personal_effort) : '_none'} 
                        onValueChange={(v) => updateTaskField(task.task_id, 'task_actual_personal_effort', v === '_none' ? null : parseInt(v))}
                        disabled={task.work_area_id !== 'Personal'}
                      >
                        <SelectTrigger className={`h-7 w-14 text-sm text-center border orka-border-subtle ${task.work_area_id !== 'Personal' ? 'opacity-30' : 'orka-text-secondary'}`}>
                          <SelectValue placeholder="-" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="_none">-</SelectItem>
                          {EFFORT_OPTIONS.map(e => (
                            <SelectItem key={e} value={String(e)}>{e}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </td>

                    {/* Status */}
                    <td className="p-2" data-testid={`status-cell-${task.task_id}`}>
                      <Select 
                        value={task.task_status} 
                        onValueChange={(v) => updateTaskField(task.task_id, 'task_status', v)}
                      >
                        <SelectTrigger className={`w-full h-10 rounded-lg text-xs font-bold border-0 ${STATUS_COLORS[task.task_status] || STATUS_COLORS['To Do']}`}>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {STATUS_OPTIONS.map(s => (
                            <SelectItem key={s} value={s}>
                              <span className={`px-2 py-1 rounded ${STATUS_COLORS[s]}`}>{s}</span>
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </td>

                    {/* Meeting */}
                    <td className="p-2 text-center">
                      <input
                        type="checkbox"
                        checked={task.task_is_meeting || false}
                        onChange={(e) => updateTaskField(task.task_id, 'task_is_meeting', e.target.checked)}
                        className="w-4 h-4 rounded border-slate-300 text-blue-600"
                      />
                    </td>

                    {/* Hide */}
                    <td className="p-2 text-center" data-testid={`hide-task-${task.task_id}`}>
                      <input
                        type="checkbox"
                        checked={task.task_hidden || false}
                        onChange={(e) => updateTaskField(task.task_id, 'task_hidden', e.target.checked)}
                        className="w-4 h-4 rounded border-slate-300 orka-text-secondary"
                      />
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination Controls */}
        <div className="flex items-center justify-between px-4 py-3 border-t border orka-border-subtle bg-stone-50 dark:bg-slate-800">
          {/* Page size selector */}
          <div className="flex items-center gap-2">
            <span className="text-sm orka-text-secondary">Show</span>
            <Select 
              value={String(pageSize)} 
              onValueChange={(v) => {
                setPageSize(parseInt(v));
                setCurrentPage(1);
              }}
            >
              <SelectTrigger className="h-8 w-20 text-sm border orka-border-subtle" data-testid="page-size-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {PAGE_SIZE_OPTIONS.map(size => (
                  <SelectItem key={size} value={String(size)}>{size}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <span className="text-sm orka-text-secondary">per page</span>
          </div>
          
          {/* Showing X-Y of Z */}
          <div className="text-sm orka-text-secondary">
            Showing <span className="font-medium orka-text-primary">{startItem}-{endItem}</span> of <span className="font-medium orka-text-primary">{filteredTasks.length}</span> tasks
            {filteredTasks.length !== totalTaskCount && (
              <span className="orka-text-tertiary ml-1">({totalTaskCount} total)</span>
            )}
          </div>
          
          {/* Page navigation */}
          <div className="flex items-center gap-1">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setCurrentPage(1)}
              disabled={currentPage === 1}
              className="h-8 w-8 p-0 border orka-border-subtle"
              data-testid="pagination-first"
            >
              <ChevronLeft className="h-4 w-4" />
              <ChevronLeft className="h-4 w-4 -ml-2.5" />
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
              disabled={currentPage === 1}
              className="h-8 w-8 p-0 border orka-border-subtle"
              data-testid="pagination-prev"
            >
              <ChevronLeft className="h-4 w-4" />
            </Button>
            
            <div className="flex items-center gap-1 mx-2">
              <span className="text-sm orka-text-secondary">Page</span>
              <Input
                type="number"
                min={1}
                max={totalPages || 1}
                value={currentPage}
                onChange={(e) => {
                  const val = parseInt(e.target.value);
                  if (val >= 1 && val <= totalPages) {
                    setCurrentPage(val);
                  }
                }}
                className="h-8 w-14 text-sm text-center border orka-border-subtle"
                data-testid="pagination-input"
              />
              <span className="text-sm orka-text-secondary">of {totalPages || 1}</span>
            </div>
            
            <Button
              variant="outline"
              size="sm"
              onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
              disabled={currentPage >= totalPages}
              className="h-8 w-8 p-0 border orka-border-subtle"
              data-testid="pagination-next"
            >
              <ChevronRight className="h-4 w-4" />
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setCurrentPage(totalPages)}
              disabled={currentPage >= totalPages}
              className="h-8 w-8 p-0 border orka-border-subtle"
              data-testid="pagination-last"
            >
              <ChevronRight className="h-4 w-4" />
              <ChevronRight className="h-4 w-4 -ml-2.5" />
            </Button>
          </div>
        </div>
      </div>

      {/* Bulk Edit Modal */}
      <Dialog open={showBulkEdit} onOpenChange={setShowBulkEdit}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="text-lg font-bold orka-text-primary">
              Bulk Edit {selectedTasks.size} Tasks
            </DialogTitle>
            <button 
              onClick={() => setShowBulkEdit(false)}
              className="absolute right-4 top-4"
            >
              <X className="h-4 w-4" />
            </button>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div>
              <label className="text-sm font-medium orka-text-secondary mb-1.5 block">Field to Update</label>
              <Select value={bulkEditField} onValueChange={setBulkEditField}>
                <SelectTrigger data-testid="bulk-edit-field-select">
                  <SelectValue placeholder="Select field" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="task_status">Status</SelectItem>
                  <SelectItem value="task_priority">Priority</SelectItem>
                  <SelectItem value="work_area_id">Work Area</SelectItem>
                  <SelectItem value="task_estimated_effort">Est. Effort</SelectItem>
                  <SelectItem value="task_delivery_date">Date</SelectItem>
                  <SelectItem value="task_is_meeting">Is Meeting</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {bulkEditField && (
              <div>
                <label className="text-sm font-medium orka-text-secondary mb-1.5 block">New Value</label>
                {bulkEditField === 'status' && (
                  <Select value={bulkEditValue} onValueChange={setBulkEditValue}>
                    <SelectTrigger data-testid="bulk-edit-value-select"><SelectValue placeholder="Select status" /></SelectTrigger>
                    <SelectContent>{STATUS_OPTIONS.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                  </Select>
                )}
                {bulkEditField === 'task_priority' && (
                  <Select value={bulkEditValue} onValueChange={setBulkEditValue}>
                    <SelectTrigger data-testid="bulk-edit-value-select"><SelectValue placeholder="Select priority" /></SelectTrigger>
                    <SelectContent>{PRIORITY_OPTIONS.map(p => <SelectItem key={p.value} value={String(p.value)}>{p.label}</SelectItem>)}</SelectContent>
                  </Select>
                )}
                {bulkEditField === 'work_area_id' && (
                  <Select value={bulkEditValue} onValueChange={setBulkEditValue}>
                    <SelectTrigger data-testid="bulk-edit-value-select"><SelectValue placeholder="Select work area" /></SelectTrigger>
                    <SelectContent>{workAreaNames.map(w => <SelectItem key={w} value={w}>{w}</SelectItem>)}</SelectContent>
                  </Select>
                )}
                {bulkEditField === 'task_estimated_effort' && (
                  <Select value={bulkEditValue} onValueChange={setBulkEditValue}>
                    <SelectTrigger data-testid="bulk-edit-value-select"><SelectValue placeholder="Select effort" /></SelectTrigger>
                    <SelectContent>{EFFORT_OPTIONS.map(e => <SelectItem key={e} value={String(e)}>{e}</SelectItem>)}</SelectContent>
                  </Select>
                )}
                {bulkEditField === 'task_delivery_date' && (
                  <DatePickerWithStats
                    value={bulkEditValue}
                    onChange={(val) => setBulkEditValue(val)}
                    triggerClassName="h-9 w-full"
                  />
                )}
                {bulkEditField === 'task_is_meeting' && (
                  <Select value={bulkEditValue} onValueChange={setBulkEditValue}>
                    <SelectTrigger data-testid="bulk-edit-value-select"><SelectValue placeholder="Select option" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="true">Yes</SelectItem>
                      <SelectItem value="false">No</SelectItem>
                    </SelectContent>
                  </Select>
                )}
              </div>
            )}
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setShowBulkEdit(false)}
              className="h-10 px-4 rounded-full border orka-border-subtle orka-text-secondary"
            >
              Cancel
            </Button>
            <Button
              onClick={applyBulkEdit}
              disabled={!bulkEditField || bulkEditValue === ''}
              className="h-10 px-4 rounded-full bg-blue-600 text-white disabled:opacity-50"
              data-testid="bulk-edit-apply-button"
            >
              Apply
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Import CSV Modal - Two Step */}
      <Dialog open={showImportModal} onOpenChange={(open) => { if (!open) resetImportState(); else setShowImportModal(open); }}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto p-0 orka-surface-raised border border orka-border-subtle">
          <DialogHeader className="sticky top-0 z-20 orka-surface-raised px-6 pt-6 pb-3 border-b border orka-border-subtle">
            <DialogTitle className="flex items-center gap-2 orka-text-primary">
              <FileText className="h-5 w-5" />
              Import Tasks
            </DialogTitle>
            <DialogDescription className="orka-text-secondary">
              {importProgress
                ? importProgress
                : importStep === 1
                  ? `Review and adjust the column mapping for your ${importHeaders.length} CSV columns before importing.`
                  : importStep === 2
                    ? `${importRowCount} tasks parsed and ready to import.`
                    : 'Reading file...'
              }
            </DialogDescription>
          </DialogHeader>
          
          <div className="px-6 py-4 bg-stone-50/50 dark:bg-slate-900/50">
            {importProgress ? (
              <div className="flex flex-col items-center justify-center py-12">
                <Loader2 className="h-8 w-8 animate-spin text-indigo-500 mb-4" />
                <p className="text-sm orka-text-secondary">{importProgress}</p>
              </div>
            ) : importStep === 3 ? (
              /* Error review step */
              <div className="space-y-4">
                <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 rounded-lg p-4">
                  <div className="flex items-center gap-2 text-red-700 mb-3">
                    <AlertCircle className="h-5 w-5" />
                    <span className="font-semibold">Import Errors ({importErrors.length})</span>
                  </div>
                  <div className="max-h-[350px] overflow-auto space-y-1">
                    {importErrors.map((err, i) => (
                      <p key={i} className="text-sm text-red-600 font-mono orka-surface-raised rounded px-3 py-1.5 border border-red-100">{err}</p>
                    ))}
                  </div>
                </div>
                <p className="text-sm text-muted-foreground">
                  Copy the errors above and share with support if the issue persists. You can try importing again with a smaller file or in a different format (CSV/JSON).
                </p>
              </div>
            ) : importStep === 1 ? (
              /* Step 1 — Column mapping review (CSV only) */
              <div className="space-y-4" data-testid="import-mapping-step">
                <div className="rounded-lg border border-indigo-200 dark:border-indigo-800 bg-indigo-50 dark:bg-indigo-950/40 p-4 text-sm">
                  <p className="font-semibold text-indigo-900 dark:text-indigo-200 mb-1">Review column mapping</p>
                  <p className="text-indigo-700 dark:text-indigo-300 text-xs leading-relaxed">
                    Map each column from your CSV to an Orka field. Orka auto-detected matches where it could — adjust any that look wrong.
                    At minimum, one column must map to <span className="font-mono bg-indigo-100 dark:bg-indigo-900/60 px-1 py-0.5 rounded text-indigo-900 dark:text-indigo-100">task_title</span>.
                    Values that don't match the target field's expected format will be safely dropped.
                  </p>
                </div>

                {rowMismatch.count > 0 && (
                  <div className="rounded-lg border border-amber-300 dark:border-amber-800 bg-amber-50 dark:bg-amber-950/40 p-4 text-sm" data-testid="row-mismatch-banner">
                    <p className="font-semibold text-amber-900 dark:text-amber-200 mb-1">
                      {rowMismatch.count} row{rowMismatch.count === 1 ? '' : 's'} skipped due to malformed CSV
                    </p>
                    <p className="text-amber-800 dark:text-amber-300 text-xs leading-relaxed mb-2">
                      These rows don't have the expected <span className="font-mono font-semibold">{importHeaders.length}</span> columns,
                      so importing them would shift every cell one position to the left and corrupt subsequent fields (which is why you might
                      see values like <span className="font-mono">"Done"</span> flagged as invalid in the Priority column below).
                      The usual cause is an unescaped comma or line break inside a cell — in Excel/Sheets, re-export with "Quote all text fields" enabled.
                    </p>
                    <details className="text-xs text-amber-700 dark:text-amber-300">
                      <summary className="cursor-pointer font-medium hover:underline">Show a few examples</summary>
                      <ul className="mt-2 space-y-1 font-mono text-[11px]">
                        {rowMismatch.samples.map((s, i) => (
                          <li key={i} className="break-all">
                            <span className="font-sans font-medium">Row {s.row}</span> — got {s.got} cells, expected {s.expected}:
                            <span className="ml-1 bg-amber-100 dark:bg-amber-900/40 px-1 py-0.5 rounded">{s.preview}{s.preview.length >= 140 ? '…' : ''}</span>
                          </li>
                        ))}
                      </ul>
                    </details>
                  </div>
                )}

                {/* Column headers row */}
                <div className="grid grid-cols-12 gap-3 px-3 text-[11px] font-semibold uppercase tracking-wide orka-text-tertiary">
                  <div className="col-span-4">CSV column</div>
                  <div className="col-span-5">Maps to Orka field</div>
                  <div className="col-span-3">Sample (row 2)</div>
                </div>

                <div className="space-y-2" data-testid="mapping-rows-scroll">
                  {importHeaders.map((header, idx) => {
                    const currentField = columnMapping[idx] || '__skip__';
                    const sampleValue = (() => {
                      try {
                        const first = (importFileText || '').split('\n')[1] || '';
                        const parts = first.split(',');
                        let raw = parts[idx] || '';
                        raw = raw.replace(/^"|"$/g, '').slice(0, 120);
                        return raw;
                      } catch { return ''; }
                    })();
                    const v = mappingValidation[idx];
                    const hasIssues = v && v.invalid > 0;
                    const allInvalid = v && v.total > 0 && v.invalid === v.total;
                    const isSkipped = currentField === '__skip__';
                    return (
                      <div
                        key={idx}
                        className={`grid grid-cols-12 gap-3 items-start p-3 rounded-lg border transition-colors ${
                          hasIssues
                            ? 'bg-red-50 dark:bg-red-950/30 border-red-200 dark:border-red-900'
                            : isSkipped
                              ? 'orka-surface-subtle/40 border orka-border-subtle opacity-60'
                              : 'orka-surface-raised/60 border orka-border-subtle'
                        }`}
                      >
                        <div className="col-span-4 min-w-0">
                          <div className="font-mono text-sm orka-text-primary break-words" title={header}>
                            {header || <span className="italic orka-text-tertiary">(column {idx + 1})</span>}
                          </div>
                        </div>

                        <div className="col-span-5 min-w-0">
                          <select
                            value={currentField}
                            onChange={(e) => updateColumnMapping(idx, e.target.value)}
                            className={`w-full h-9 text-sm px-3 rounded-md border orka-surface-raised orka-text-primary focus:outline-none focus:ring-2 focus:ring-indigo-500 dark:focus:ring-indigo-400 ${
                              allInvalid ? 'border-red-400 dark:border-red-500' : 'border orka-border-default hover:border-slate-400 dark:hover:border-slate-500'
                            }`}
                            data-testid={`map-col-${idx}`}
                          >
                            <option value="__skip__">— skip this column —</option>
                            {DB_FIELDS.map(f => (
                              <option key={f.key} value={f.key}>
                                {f.label}{f.required ? ' *' : ''}
                              </option>
                            ))}
                          </select>
                          {hasIssues && (
                            <div
                              className="mt-2 text-xs leading-relaxed text-red-700 dark:text-red-300"
                              data-testid={`map-col-${idx}-warning`}
                            >
                              <span className="font-semibold">{v.invalid} of {v.total} rows</span>{' '}
                              {allInvalid ? 'are invalid' : "don't fit"}
                              {v.samples?.[0] && (
                                <> — e.g. <span className="font-mono bg-red-100 dark:bg-red-900/40 px-1 py-0.5 rounded">"{String(v.samples[0].value).slice(0, 50)}"</span> ({v.samples[0].error})</>
                              )}.
                              <div className="mt-0.5 text-red-600/80 dark:text-red-400/80">
                                Invalid values will be dropped during import.
                              </div>
                            </div>
                          )}
                        </div>

                        <div className="col-span-3 min-w-0">
                          <div className="text-xs orka-text-secondary font-mono break-words" title={sampleValue}>
                            {sampleValue ? sampleValue : <span className="italic orka-text-tertiary font-sans">(empty)</span>}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>

                <div className="flex items-center justify-between text-sm pt-2 border-t border orka-border-subtle">
                  <div className="orka-text-secondary">
                    <span className="font-medium text-slate-800 dark:text-slate-200">
                      {Object.values(columnMapping).filter(Boolean).length}
                    </span>
                    {' of '}{importHeaders.length} columns mapped
                    {(() => {
                      const totalInvalid = Object.values(mappingValidation).reduce((acc, v) => acc + (v?.invalid || 0), 0);
                      if (totalInvalid > 0) {
                        return (
                          <span className="ml-3 text-red-600 dark:text-red-400 font-medium">
                            · {totalInvalid} invalid cell{totalInvalid === 1 ? '' : 's'} will be dropped
                          </span>
                        );
                      }
                      return null;
                    })()}
                  </div>
                  <span className={`font-medium ${Object.values(columnMapping).includes('task_title') ? 'text-emerald-700 dark:text-emerald-400' : 'text-red-700 dark:text-red-400'}`}>
                    {Object.values(columnMapping).includes('task_title') ? '✓ task_title is mapped' : '✗ task_title is required'}
                  </span>
                </div>
              </div>
            ) : importStep === 2 ? (
              /* Preview parsed tasks */
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <p className="text-sm">
                    <span className="font-semibold text-green-600">{importRowCount}</span> tasks ready to import
                  </p>
                </div>

                {importErrors.length > 0 && (
                  <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 rounded-lg p-3">
                    <div className="flex items-center gap-2 text-red-600 mb-2">
                      <AlertCircle className="h-4 w-4" />
                      <span className="font-medium text-sm">Import Errors</span>
                    </div>
                    <ul className="text-sm text-red-600 list-disc pl-5">
                      {importErrors.slice(0, 5).map((err, i) => <li key={i}>{err}</li>)}
                      {importErrors.length > 5 && <li>...and {importErrors.length - 5} more</li>}
                    </ul>
                  </div>
                )}

                {importData.length > 0 && (
                  <div className="max-h-[300px] overflow-auto border border orka-border-subtle rounded-lg">
                    <table className="w-full text-sm">
                      <thead className="bg-stone-50 dark:bg-slate-800 sticky top-0">
                        <tr>
                          <th className="p-2 text-left font-medium">Title</th>
                          <th className="p-2 text-left font-medium">Date</th>
                          <th className="p-2 text-left font-medium">Status</th>
                          <th className="p-2 text-left font-medium">Priority</th>
                          <th className="p-2 text-left font-medium">Work Area</th>
                        </tr>
                      </thead>
                      <tbody>
                        {importData.slice(0, 20).map((row, i) => (
                          <tr key={i} className="border-t border-stone-100 dark:border-slate-700">
                            <td className="p-2 truncate max-w-[200px]">{row.task_title}</td>
                            <td className="p-2 font-mono text-xs">{row.task_local_date || '-'}</td>
                            <td className="p-2">{row.task_status || 'To Do'}</td>
                            <td className="p-2">{row.task_priority ?? '3'}</td>
                            <td className="p-2">{row.work_area_id || '-'}</td>
                            <td className="p-2">{row.personal_sub_area || '-'}</td>
                          </tr>
                        ))}
                        {importRowCount > 20 && (
                          <tr><td colSpan={5} className="p-2 text-center text-slate-400">...and {importRowCount - 20} more</td></tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            ) : (
              <div className="flex flex-col items-center justify-center py-12">
                <Loader2 className="h-8 w-8 animate-spin text-indigo-500 mb-4" />
                <p className="text-sm orka-text-secondary">Parsing file...</p>
              </div>
            )}
          </div>

          <DialogFooter className="sticky bottom-0 z-20 px-6 py-3 border-t border orka-border-subtle orka-surface-raised">
            <Button variant="outline" onClick={resetImportState}>
              {importStep === 3 ? 'Close' : 'Cancel'}
            </Button>
            {importStep === 3 && importTasksRef.current?.length > 0 && (
              <Button onClick={() => { setImportStep(2); setImportErrors([]); }} variant="outline">
                Back to Preview
              </Button>
            )}
            {importStep === 2 && importFileKind === 'csv' && (
              <Button variant="outline" onClick={() => setImportStep(1)} data-testid="back-to-mapping">
                Back to mapping
              </Button>
            )}
            {importStep === 1 && (
              <Button
                onClick={applyMappingAndPreview}
                disabled={!Object.values(columnMapping).includes('task_title')}
                data-testid="confirm-mapping"
              >
                Next — Preview {importRowCount} tasks
              </Button>
            )}
            {importStep === 2 && (
              <Button 
                onClick={executeImport} 
                disabled={!importRowCount || importing} 
                data-testid="execute-import"
              >
                {importing ? 'Importing...' : `Import ${importRowCount} Tasks`}
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete All Data Modal - Double Opt-In */}
      <Dialog open={showDeleteAllModal} onOpenChange={(open) => {
        setShowDeleteAllModal(open);
        if (!open) {
          setDeleteConfirmText('');
          setDeleteMode('soft');
          setShowPurgeConfirm(false);
        }
      }}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <AlertTriangle className="h-5 w-5" />
              Delete All Data
            </DialogTitle>
          </DialogHeader>
          
          <div className="py-4 space-y-4">
            {/* Mode Selection */}
            <div className="flex gap-2 p-1 bg-muted rounded-lg">
              <Button 
                variant={deleteMode === 'soft' ? 'default' : 'ghost'}
                className="flex-1"
                onClick={() => { setDeleteMode('soft'); setDeleteConfirmText(''); setShowPurgeConfirm(false); }}
              >
                Soft Delete
              </Button>
              <Button 
                variant={deleteMode === 'purge' ? 'destructive' : 'ghost'}
                className="flex-1"
                onClick={() => { setDeleteMode('purge'); setDeleteConfirmText(''); setShowPurgeConfirm(false); }}
              >
                Purge Database
              </Button>
            </div>

            {deleteMode === 'soft' ? (
              <>
                <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 rounded-lg p-4">
                  <p className="text-sm text-amber-700 font-medium mb-2">
                    Soft Delete - Marks {totalTaskCount} tasks as deleted
                  </p>
                  <p className="text-sm text-amber-600">
                    Tasks will be hidden but records are kept. Calendar sync will update existing mappings.
                  </p>
                </div>
                
                <p className="text-sm text-muted-foreground">
                  Type <strong>DELETE ALL</strong> to confirm:
                </p>
                <Input
                  value={deleteConfirmText}
                  onChange={(e) => setDeleteConfirmText(e.target.value)}
                  placeholder="Type DELETE ALL to confirm"
                  className="border-amber-200 focus:border-amber-400"
                  data-testid="delete-confirm-input"
                />
              </>
            ) : (
              <>
                <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 rounded-lg p-4">
                  <p className="text-sm text-red-700 font-medium mb-2">
                    ⚠️ PERMANENT PURGE - NO RECOVERY POSSIBLE
                  </p>
                  <p className="text-sm text-red-600">
                    This will permanently delete ALL tasks ({totalTaskCount}) AND all calendar sync mappings. 
                    Next calendar sync will create fresh records.
                  </p>
                </div>
                
                {!showPurgeConfirm ? (
                  <>
                    <p className="text-sm text-muted-foreground">
                      Type <strong>PURGE ALL DATA</strong> to proceed:
                    </p>
                    <Input
                      value={deleteConfirmText}
                      onChange={(e) => setDeleteConfirmText(e.target.value)}
                      placeholder="Type PURGE ALL DATA"
                      className="border-red-200 focus:border-red-400"
                      data-testid="purge-confirm-input"
                    />
                  </>
                ) : (
                  <div className="bg-red-100 border-2 border-red-400 rounded-lg p-4 text-center">
                    <p className="text-red-700 font-bold mb-2">
                      🚨 FINAL CONFIRMATION
                    </p>
                    <p className="text-sm text-red-600 mb-4">
                      Click "Purge Everything" to permanently delete all data. This cannot be undone.
                    </p>
                  </div>
                )}
              </>
            )}
          </div>

          <DialogFooter className="flex-col sm:flex-row gap-2">
            <Button 
              variant="outline" 
              onClick={() => { setShowDeleteAllModal(false); setDeleteConfirmText(''); setShowPurgeConfirm(false); setDeleteMode('soft'); }}
            >
              Cancel
            </Button>
            
            {deleteMode === 'soft' ? (
              <Button
                variant="default"
                onClick={deleteAllData}
                disabled={deleteConfirmText !== 'DELETE ALL' || deleting}
                className="bg-amber-600 hover:bg-amber-700"
                data-testid="confirm-delete-all"
              >
                {deleting ? 'Deleting...' : 'Mark All as Deleted'}
              </Button>
            ) : (
              <Button
                variant="destructive"
                onClick={purgeAllData}
                disabled={(deleteConfirmText !== 'PURGE ALL DATA' && !showPurgeConfirm) || deleting}
                className="bg-red-600 hover:bg-red-700"
                data-testid="confirm-purge-all"
              >
                {deleting ? 'Purging...' : showPurgeConfirm ? '🔥 Purge Everything' : 'Continue to Final Confirmation'}
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Import Dialog with Template Download and Drag/Drop */}
      <Dialog open={showImportDialog} onOpenChange={setShowImportDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Upload className="h-5 w-5" />
              Import Tasks
            </DialogTitle>
            <DialogDescription>
              Upload a CSV or JSON file to import tasks. JSON export is recommended for migration between environments.
            </DialogDescription>
          </DialogHeader>
          
          <div className="py-4 space-y-4">
            {/* Template Download */}
            <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 rounded-lg p-4">
              <p className="text-sm text-blue-700 mb-2">
                Need a template? Download our CSV template with all supported fields.
              </p>
              <button
                onClick={downloadTemplate}
                className="text-sm text-blue-600 hover:text-blue-800 underline font-medium flex items-center gap-1"
                data-testid="download-template"
              >
                <Download className="w-4 h-4" />
                Download Template (orka_import_template.csv)
              </button>
            </div>

            {/* Drag and Drop Upload Area */}
            <div
              className={`border-2 border-dashed rounded-xl p-8 text-center transition-colors cursor-pointer
                ${dragOver ? 'border-blue-500 bg-blue-50' : 'border-stone-300 hover:border-stone-400'}`}
              onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
              onDragLeave={() => setDragOver(false)}
              onDrop={(e) => {
                e.preventDefault();
                setDragOver(false);
                const file = e.dataTransfer.files[0];
                if (file && (file.name.endsWith('.csv') || file.name.endsWith('.json'))) {
                  const fakeEvent = { target: { files: [file] } };
                  handleFileUpload(fakeEvent);
                  setShowImportDialog(false);
                } else {
                  toast.error('Please drop a CSV or JSON file');
                }
              }}
              onClick={() => {
                fileInputRef.current?.click();
                setShowImportDialog(false);
              }}
            >
              <Upload className={`h-10 w-10 mx-auto mb-3 ${dragOver ? 'text-blue-500' : 'text-stone-400'}`} />
              <p className="text-sm font-medium orka-text-secondary">
                {dragOver ? 'Drop your file here' : 'Drag & drop your CSV or JSON file here'}
              </p>
              <p className="text-xs orka-text-tertiary dark:text-slate-500 mt-1">or click to browse</p>
            </div>

            {/* Supported Fields Info */}
            <div className="text-xs orka-text-tertiary">
              <p className="font-medium mb-1">Supported fields ({DB_FIELDS.length} total):</p>
              <p className="text-slate-400">
                {DB_FIELDS.map(f => f.label).join(', ')}
              </p>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowImportDialog(false)}>
              Cancel
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Edit Task Modal */}
      <TaskModal
        open={showEditModal}
        onOpenChange={(open) => {
          setShowEditModal(open);
          if (!open) setEditingTaskData(null);
        }}
        task={editingTaskData}
        selectedDate={editingTaskData?.task_delivery_date ? new Date(editingTaskData.task_delivery_date) : new Date()}
        onSave={handleEditModalSave}
      />
      <WorkAreaEditorModal
        open={showWorkAreaEditor}
        onOpenChange={setShowWorkAreaEditor}
        onWorkAreasChanged={() => { fetchTasks(); refreshWorkAreas(); }}
      />
    </Wrapper>
  );
};

export default DataView;
